// DESCRIPTION: Interstitial — runs the /setup/finalize request and shows a
// live checklist while the server creates pages and seeds the sample event.

import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import WizardShell from '../components/WizardShell';

/**
 * Fake-but-believable progressive ticks so the user has something to watch
 * even on fast servers where the finalize call returns in 200ms.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Function} props.onDone  (finalizeResult) => void
 * @param {Function} props.onError (error) => void
 *
 * @return {JSX.Element}
 */
export default function FinalizingStep( { onDone, onError } ) {
	const [ checks, setChecks ] = useState( {
		prefs: false,
		pages: false,
		sample: false,
		wrapping: false,
	} );

	useEffect( () => {
		let cancelled = false;
		const tickTimers = [];

		// Visual ticks — give the user something to watch.
		tickTimers.push(
			setTimeout(
				() =>
					! cancelled &&
					setChecks( ( c ) => ( { ...c, prefs: true } ) ),
				250
			)
		);
		tickTimers.push(
			setTimeout(
				() =>
					! cancelled &&
					setChecks( ( c ) => ( { ...c, pages: true } ) ),
				700
			)
		);
		tickTimers.push(
			setTimeout(
				() =>
					! cancelled &&
					setChecks( ( c ) => ( { ...c, sample: true } ) ),
				1200
			)
		);

		( async () => {
			try {
				const result = await apiFetch( {
					path: '/nemtly-booking/v1/setup/finalize',
					method: 'POST',
				} );

				if ( cancelled ) {
					return;
				}

				setChecks( ( c ) => ( { ...c, wrapping: true } ) );

				// Hold for a beat so the last check is visible.
				setTimeout( () => {
					if ( ! cancelled ) {
						onDone( result );
					}
				}, 500 );
			} catch ( err ) {
				if ( ! cancelled ) {
					onError( err );
				}
			}
		} )();

		return () => {
			cancelled = true;
			tickTimers.forEach( clearTimeout );
		};
	}, [ onDone, onError ] );

	const rows = [
		{
			key: 'prefs',
			label: __( 'Saving your preferences', 'nemtly-booking' ),
		},
		{
			key: 'pages',
			label: __( 'Creating your booking pages', 'nemtly-booking' ),
		},
		{
			key: 'sample',
			label: __( 'Setting up a sample event', 'nemtly-booking' ),
		},
		{
			key: 'wrapping',
			label: __( 'Wrapping things up', 'nemtly-booking' ),
		},
	];

	return (
		<WizardShell stepKey="finalizing" progress={ 100 } hideFooter>
			<div className="nemtly-wizard-finalizing">
				<h2 className="nemtly-wizard-headline">
					{ __( 'Setting things up…', 'nemtly-booking' ) }
				</h2>
				<p className="nemtly-wizard-lede">
					{ __(
						'This should only take a moment.',
						'nemtly-booking'
					) }
				</p>

				<ul className="nemtly-wizard-checklist">
					{ rows.map( ( row ) => (
						<li
							key={ row.key }
							className={ `nemtly-wizard-checklist__item ${
								checks[ row.key ] ? 'is-done' : 'is-pending'
							}` }
						>
							<span
								className="nemtly-wizard-checklist__icon"
								aria-hidden="true"
							>
								{ checks[ row.key ] ? '✓' : '·' }
							</span>
							<span>{ row.label }</span>
						</li>
					) ) }
				</ul>
			</div>
		</WizardShell>
	);
}
